import TextButton from 'components/Luxkit/Button/TextButton'
import LinePhoneIcon from 'components/Luxkit/Icons/line/LinePhoneIcon'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { replace } from 'connected-react-router'
import config from 'constants/config'
import { queryKeyFilters } from 'constants/url'
import ContactContext from 'contexts/contactContext'
import useOfferList from 'hooks/Offers/useOfferList'
import { useAppDispatch } from 'hooks/reduxHooks'
import { usePlaceById, usePlaceByName } from 'hooks/usePlace'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import LineIncomingCallIcon from 'components/Luxkit/Icons/line/LineIncomingCallIcon'

interface Props {
  location: string;
  filters: App.OfferListFilters;
}

interface MappedStateProps {
  account: App.AuthAccount;
  windowSearch: string;
}

const NoSearchResultGroup = styled(Group)`
  padding-top: ${rem(48)};
`

const BreakPointGroup = styled(CSSBreakpoint)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
`

function HotelSearchDestinationNoResults(props: Props & MappedStateProps) {
  const { location, filters, windowSearch } = props
  const currentContact = useContext(ContactContext)
  const localContact = currentContact.local
  const place = usePlaceById(filters.destinationId)
  const countryName = useMemo(() => place ? config.regions.find(region => region.code === place.countryCode) : undefined, [place])
  const country = usePlaceByName(countryName?.name)
  const isCountry = country?.id === filters.destinationId
  const callbackType: App.GenesysAgentCallbackType = 'HOTELS_AND_VILLAS'

  const showModal = useContext(ModalContext)

  const expandedFilters = useMemo((): App.OfferListFilters => ({
    ...filters,
    distanceGt: 0,
    distanceEq: undefined,
  }), [filters])

  const bedbankFilters = useMemo((): App.OfferListFilters => ({
    ...expandedFilters,
    offerTypes: ['bedbank_hotel'],
  }), [expandedFilters])
  const dispatch = useAppDispatch()

  const filterCount = (filters.holidayTypes?.length ?? 0) + (filters.amenities?.length ?? 0) + (filters.inclusions?.length ?? 0) + (filters?.priceLte ? 1 : 0) + (filters.locations?.length ?? 0) + (filters.customerRatingGte ? 1 : 0)
  const hasFiltersApplied = filterCount > 0

  const onResetAllClick = useCallback(() => {
    const newSearch = deleteSearchParams(windowSearch, ...queryKeyFilters)
    dispatch(replace({ search: newSearch }))
  }, [dispatch, windowSearch])

  const offerList = useOfferList(expandedFilters)
  const bedbankOfferList = useOfferList(bedbankFilters)

  const hasNearby = offerList.offerIds.length > 0 || bedbankOfferList.offerIds.length > 0

  const pushToCountry = useCallback(() => {
    if (!country) return
    const newSearch = setManySearchParamValues(windowSearch, [{ paramName: 'destinationId', paramValue: country.id }, { paramName: 'destinationName', paramValue: country.name }])
    dispatch(replace({ search: newSearch }))
  }, [country, dispatch, windowSearch])

  const handleCallbackClick = () => { showModal(<RequestAgentCallbackModal type={callbackType} filters={filters} location={location}/>) }

  return (
    <Group gap={40} direction="vertical" fullWidth>
      <Group gap={24} direction="vertical" fullWidth horizontalAlign="center">
        <NoSearchResultGroup gap={8} direction="vertical" fullWidth horizontalAlign="center">
          <Heading variant="heading3" align="center">
            {location && `Sorry, we couldn't find any offers in ${location}`}
            {!location && 'Sorry, we couldn’t find any offers matching your search'}
          </Heading>
          <BreakPointGroup min="tablet">
            <BodyText variant="medium">
              Speak to one of our travel experts{' '}
              <TextLink href={`tel:${localContact.number}`} rel="nofollow">
                {localContact.humanReadable}
              </TextLink>
            </BodyText>
            {(!country || isCountry || hasNearby) && <BodyText variant="medium">
              {hasFiltersApplied ? 'Or try a different search' : 'Or try a different location'}
            </BodyText>}
            {!!country && !isCountry && !hasNearby &&
              <BodyText variant="medium">OR</BodyText>
            }
          </BreakPointGroup>
          {!!country && !isCountry && !hasNearby && <TextButton fit="mobile-full-width" startIcon={<LineSearchIcon />} kind="secondary" onClick={pushToCountry}>Discover all offers in {country.name}</TextButton>}
          <TextButton fit="mobile-full-width" startIcon={<LineIncomingCallIcon/>} kind="secondary" onClick={handleCallbackClick}>Request a callback</TextButton>
          <BreakPointGroup max="mobile">
            <BodyText variant="medium">OR</BodyText>
            <TextButton startIcon={<LinePhoneIcon />} fit="mobile-full-width" kind="secondary" href={`tel:${localContact.number}`} rel="nofollow">Speak to a travel expert</TextButton>
          </BreakPointGroup>
          <CSSBreakpoint max="desktop">
            {hasFiltersApplied && <TextLink onClick={onResetAllClick}>Reset filters</TextLink>}
          </CSSBreakpoint>
        </NoSearchResultGroup>
      </Group>
    </Group>
  )
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    account: state.auth.account,
    windowSearch: state.router.location.search,
  }
}

export default connect(mapStateToProps)(HotelSearchDestinationNoResults)
